import {
  ENVIRONMENTS,
  normalizeHostname,
  normalizeOrigin,
  type TraceEnvironment,
} from '@traceio-org/platform-contracts';

const LOCAL_API_URL = 'http://localhost:4720';
const LOCAL_HUB_ORIGIN = 'http://localhost:4710';

export interface AdminConfig {
  environment: TraceEnvironment;
  /** platform-api base URL (origin plus optional path, no trailing slash). */
  platformApiUrl: string;
  /** The account hub people sign in at; it must be the registry's hub origin. */
  hubOrigin: string;
  /** Trace Admin's own Auth0 SPA registration; null means development identity (development and test). */
  auth0: { domain: string; clientId: string } | null;
}

export type AdminConfigResult =
  { ok: true; config: AdminConfig; warnings: string[] } | { ok: false; errors: string[] };

type Env = Record<string, string | boolean | undefined>;

function read(env: Env, name: string): string | undefined {
  const value = env[name];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function parseBaseUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.username || url.password || url.search || url.hash) return null;
    return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}

/** Development identity exists only here; every other environment signs staff in through Auth0. */
function isLocal(environment: TraceEnvironment): boolean {
  return environment === 'development' || environment === 'test';
}

/**
 * Validates the build-time environment once at startup. Security relevant, so spelled out:
 * - Only the Vite dev server falls back to the local defaults; a build must name every value.
 * - Staging and production need https for platform-api and the hub, and Trace Admin's own Auth0
 *   registration: development identity does not exist there, so without Auth0 nobody could sign in and
 *   the app refuses to start instead of half working.
 * - Locally a partial Auth0 configuration falls back to development identity with a warning.
 * The registry check at startup (src/registry.ts) is the second, independent guard.
 */
export function loadAdminConfig(env: Env, options: { dev: boolean }): AdminConfigResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  let environment: TraceEnvironment | undefined;
  const rawEnvironment = read(env, 'VITE_TRACE_ENV');
  if (rawEnvironment === undefined) {
    if (options.dev) {
      environment = 'development';
      warnings.push('VITE_TRACE_ENV is not set; using development.');
    } else {
      errors.push('VITE_TRACE_ENV is required (development, test, staging or production).');
    }
  } else {
    environment = ENVIRONMENTS.find((e) => e === rawEnvironment);
    if (!environment) errors.push('VITE_TRACE_ENV must be development, test, staging or production.');
  }
  const deployed = environment !== undefined && !isLocal(environment);

  let platformApiUrl: string | undefined;
  const rawApiUrl = read(env, 'VITE_PLATFORM_API_URL');
  if (rawApiUrl === undefined) {
    if (options.dev) {
      platformApiUrl = LOCAL_API_URL;
      warnings.push(`VITE_PLATFORM_API_URL is not set; using ${LOCAL_API_URL}.`);
    } else {
      errors.push('VITE_PLATFORM_API_URL is required.');
    }
  } else {
    platformApiUrl = parseBaseUrl(rawApiUrl) ?? undefined;
    if (!platformApiUrl) {
      errors.push(
        'VITE_PLATFORM_API_URL must be an http or https URL without credentials, a query or a fragment.',
      );
    } else if (deployed && !platformApiUrl.startsWith('https:')) {
      errors.push('VITE_PLATFORM_API_URL must use https in staging and production.');
    }
  }

  let hubOrigin: string | undefined;
  const rawHubOrigin = read(env, 'VITE_HUB_ORIGIN');
  if (rawHubOrigin === undefined) {
    if (options.dev) {
      hubOrigin = LOCAL_HUB_ORIGIN;
      warnings.push(`VITE_HUB_ORIGIN is not set; using ${LOCAL_HUB_ORIGIN}.`);
    } else {
      errors.push('VITE_HUB_ORIGIN is required.');
    }
  } else {
    try {
      hubOrigin = normalizeOrigin(rawHubOrigin);
      if (deployed && !hubOrigin.startsWith('https:')) {
        errors.push('VITE_HUB_ORIGIN must use https in staging and production.');
      }
    } catch {
      errors.push('VITE_HUB_ORIGIN must be an origin such as https://app.trace.io.');
    }
  }

  let auth0: AdminConfig['auth0'] = null;
  const domain = read(env, 'VITE_AUTH0_DOMAIN');
  const clientId = read(env, 'VITE_AUTH0_CLIENT_ID');
  if (domain && clientId) {
    // The SDK expects a bare host (tenant or custom domain), never a URL.
    const host = normalizeHostname(domain);
    if (!host.ok || host.hostname !== domain.toLowerCase()) {
      errors.push('VITE_AUTH0_DOMAIN must be a host name such as login.trace.io.');
    } else if (!/^[A-Za-z0-9_-]{1,128}$/.test(clientId)) {
      errors.push('VITE_AUTH0_CLIENT_ID has unexpected characters.');
    } else {
      auth0 = { domain, clientId };
    }
  } else if (deployed) {
    errors.push(
      'VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID are required in staging and production: Trace Admin signs staff in through Auth0 there.',
    );
  } else if (domain || clientId) {
    const missing = domain ? 'VITE_AUTH0_CLIENT_ID' : 'VITE_AUTH0_DOMAIN';
    warnings.push(`Auth0 is only partly configured (missing ${missing}); using development identity.`);
  }

  if (errors.length > 0 || !environment || !platformApiUrl || !hubOrigin) return { ok: false, errors };
  return { ok: true, warnings, config: { environment, platformApiUrl, hubOrigin, auth0 } };
}
